// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

function buildApiUrl(path: string): string {
  return `${API_BASE_URL}${path}`;
}

function getDefaultHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
  };
}

function getFetchOptions(method: string, body?: object): RequestInit {
  return {
    method,
    headers: getDefaultHeaders(),
    mode: 'cors',
    credentials: 'omit',
    ...(body && { body: JSON.stringify(body) }),
  };
}

// Type Definitions
export interface ActivityMetric {
  name: string;        // e.g., "Payment Volume", "Trade Volume", "Support Calls"
  value: number;       // The actual count/volume
  unit?: string;       // Optional: "per day", "per month", "per hour"
  icon?: string;       // Optional: icon identifier for UI
}

export interface Customer {
  id: string;
  name: string; // Customer persona/type (e.g., "D2C Customers", "Enterprise Clients")
  description?: string;
  customerCount?: number; // How many customers of this type
  status?: 'active' | 'inactive' | 'pending';
  
  // Activity metrics - flexible property bag
  activityMetrics?: ActivityMetric[]; // Array of any metrics you want to track
  
  // Connections to other entities
  suppliers?: string[]; // Suppliers that serve this customer type
  processes?: string[]; // Processes that serve these customers (empty = ALL processes)
  staff?: string[]; // Staff members serving this customer segment
  systems?: string[]; // Systems used to service these customers
  
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface CustomerFilters {
  status?: string[];
  search?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// API Functions

/**
 * Get all customers with optional filtering
 */
export async function getAllCustomers(
  filters?: CustomerFilters
): Promise<ApiResponse<{ customers: Customer[] }>> {
  try {
    const params = new URLSearchParams();
    
    if (filters?.status && filters.status.length > 0) {
      filters.status.forEach(status => params.append('status', status));
    }
    
    if (filters?.search) {
      params.append('search', filters.search);
    }

    const url = buildApiUrl(`/customers${params.toString() ? `?${params.toString()}` : ''}`);
    console.log('Fetching customers from:', url);

    const response = await fetch(url, getFetchOptions('GET'));

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('Customers API response:', result);

    // Handle both response structures defensively
    if (result.success && result.data) {
      // If data is already wrapped in { customers: [...] }
      if (result.data.customers) {
        return result;
      }
      // If data is a flat array, wrap it
      if (Array.isArray(result.data)) {
        return {
          success: true,
          data: { customers: result.data }
        };
      }
    }

    return {
      success: false,
      error: 'Invalid response format'
    };
  } catch (error) {
    console.error('Error fetching customers:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch customers'
    };
  }
}

/**
 * Get a single customer by ID
 */
export async function getCustomerById(
  id: string
): Promise<ApiResponse<Customer>> {
  try {
    const url = buildApiUrl(`/customers?id=${id}`);
    console.log('Fetching customer:', url);

    const response = await fetch(url, getFetchOptions('GET'));

    if (!response.ok) {
      if (response.status === 404) {
        return {
          success: false,
          error: 'Customer not found'
        };
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error fetching customer:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch customer'
    };
  }
}

/**
 * Create a new customer
 */
export async function createCustomer(
  customer: Omit<Customer, 'id' | 'createdAt' | 'updatedAt'>
): Promise<ApiResponse<Customer>> {
  try {
    const url = buildApiUrl('/customers');
    console.log('Creating customer:', url, customer);

    const response = await fetch(url, getFetchOptions('POST', customer));

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error creating customer:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create customer'
    };
  }
}

/**
 * Update an existing customer
 */
export async function updateCustomer(
  id: string,
  updates: Partial<Customer>
): Promise<ApiResponse<Customer>> {
  try {
    const url = buildApiUrl(`/customers?id=${id}`);
    console.log('Updating customer:', url, updates);

    const response = await fetch(url, getFetchOptions('PUT', updates));

    if (!response.ok) {
      if (response.status === 404) {
        return {
          success: false,
          error: 'Customer not found'
        };
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error updating customer:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update customer'
    };
  }
}

/**
 * Delete a customer
 */
export async function deleteCustomer(
  id: string
): Promise<ApiResponse<void>> {
  try {
    const url = buildApiUrl(`/customers?id=${id}`);
    console.log('Deleting customer:', url);

    const response = await fetch(url, getFetchOptions('DELETE'));

    if (!response.ok) {
      if (response.status === 404) {
        return {
          success: false,
          error: 'Customer not found'
        };
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error deleting customer:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete customer'
    };
  }
}
